import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  exitFullscreen,
  fullscreenSupported,
  goFullscreen,
  isFullscreen,
  onFullscreenChange,
} from '../lib/fullscreen';

/**
 * "Play full screen" / "Exit full screen", on the screens that ask for more
 * room (the web app's rotate overlay; the embed's short-screen hint). Renders
 * nothing where the browser cannot go fullscreen (an iPhone, a frame without
 * `allow="fullscreen"`). Whether the HOST permits the control is the caller's
 * question (`PlatformCapabilities.fullscreenButton`).
 *
 * `canEnter` false keeps only the way OUT: on a portrait-locked route full
 * screen would not help, but a player who arrived there in full screen still
 * needs to be able to leave it.
 */
export function PlayFullscreenButton({ canEnter = true }: { canEnter?: boolean }) {
  const { t } = useTranslation();
  const [active, setActive] = useState(isFullscreen);

  useEffect(() => onFullscreenChange(() => setActive(isFullscreen())), []);

  if (!fullscreenSupported() || (!active && !canEnter)) return null;
  return (
    <button
      type="button"
      className="play-fullscreen-btn"
      data-testid="play-fullscreen"
      data-active={active ? 'true' : 'false'}
      onClick={() => {
        void (active ? exitFullscreen() : goFullscreen());
      }}
    >
      <svg viewBox="0 0 24 24" aria-hidden="true" className="play-fullscreen-btn__icon">
        <path
          d={
            active
              ? 'M9 4v5H4M20 9h-5V4M15 20v-5h5M4 15h5v5'
              : 'M4 9V4h5M15 4h5v5M20 15v5h-5M9 20H4v-5'
          }
        />
      </svg>
      {t(active ? 'layout.exitFullscreen' : 'layout.playFullscreen')}
    </button>
  );
}
